import { useCallback, useMemo } from 'react';

// Hooks
import useSPGroups from '@/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';

// Store
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

import type { SocialProfileType } from '@/types/socialProfileType';

const useNewActiveSocialProfile = () => {
    const {
        activeSocialProfileId,
        activeGroupId,
        activeMultiSelectionProfiles,
        updateActiveMultiProfileIds,
        updateActiveSocialProfileId
    } = useProfilesStore((state) => state);

    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateIfStale: false
    });
    const { activatedSpGroups } = useSPGroups({
        revalidateIfStale: false
    });

    const activeSocialProfile: SocialProfileType = useMemo(() => {
        if (activatedSocialProfiles?.length && activeSocialProfileId) {
            return activatedSocialProfiles.find(
                (socialProfile) => socialProfile.id === activeSocialProfileId
            );
        }

        return null;
    }, [activatedSocialProfiles, activeSocialProfileId]);

    const setActiveSocialProfile = useCallback(
        (socialProfile: SocialProfileType) => {
            updateActiveSocialProfileId(socialProfile.id);
            if (activeGroupId) {
                const activeGroup = activatedSpGroups.find((group) => group.id === activeGroupId);
                const activatedSocialProfilesGroup = activeGroup.social_profiles.filter(
                    (profile) => profile.active
                );
                const hasSocialProfileInGroup = activatedSocialProfilesGroup.find(
                    (profile) => profile.active && profile.id === socialProfile.id
                );

                if (!hasSocialProfileInGroup) {
                    const groupProfilesId = activatedSocialProfilesGroup.map(
                        (profile) => profile.id
                    );
                    updateActiveMultiProfileIds([...groupProfilesId, socialProfile.id]);
                }
                return;
            }

            if (!activeMultiSelectionProfiles?.socialProfileIds.includes(socialProfile.id)) {
                /**
                 * Check if there is already profiles added to the activeMultiSelectionProfiles
                 */
                const hasAlreadyActiveProfile = Array.isArray(
                    activeMultiSelectionProfiles?.socialProfileIds
                );
                const currentSocialProfileIds = hasAlreadyActiveProfile
                    ? activeMultiSelectionProfiles.socialProfileIds
                    : [];

                updateActiveMultiProfileIds([...currentSocialProfileIds, socialProfile.id]);
            }
        },

        [activeGroupId, activeMultiSelectionProfiles?.socialProfileIds, activatedSpGroups]
    );

    return {
        newActiveSocialProfile: activeSocialProfile,
        setNewActiveSocialProfile: setActiveSocialProfile
    };
};

const useActiveSocialProfile = () => {
    const { newActiveSocialProfile, setNewActiveSocialProfile } = useNewActiveSocialProfile();
    return {
        activeSocialProfile: newActiveSocialProfile,
        setActiveSocialProfile: setNewActiveSocialProfile
    };
};

export default useActiveSocialProfile;
